import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductCard from './ProductCard';
import Loader from '../Loader';
import { buildApiUrl } from '../../utils/api';
import { normalizeProductCard } from '../../utils/normalizeProductCard';

const RelatedProductsSection = ({ productId }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;

    const fetchRelatedProducts = async () => {
      if (!productId) return;

      setLoading(true);
      try {
        const response = await fetch(buildApiUrl(`/api/v1/customer/products/related/${productId}?limit=10`), {
          headers: { 'Content-Type': 'application/json' },
        });
        const json = await response.json();
        if (!response.ok || !json.success) throw new Error(json.message || 'Failed to load related products');

        const related = (json.data || [])
          .filter((item) => String(item.product_id ?? item.id) !== String(productId))
          .map(normalizeProductCard);

        if (alive) setProducts(related);
      } catch {
        if (alive) setProducts([]);
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchRelatedProducts();
    return () => { alive = false; };
  }, [productId]);

  return (
    <section className="mt-12">
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-primary-600">
            You may also like
          </span>
          <h2 className="mt-2 text-2xl font-black tracking-tight text-gray-900">
            Related Products
          </h2>
        </div>
        {products.length > 0 && (
          <button
            type="button"
            onClick={() => navigate('/products')}
            className="self-start rounded-full border border-gray-200 bg-white px-5 py-2.5 text-sm font-bold text-gray-700 transition hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 sm:self-auto"
          >
            View All
          </button>
        )}
      </div>

      {loading ? (
        <div className="rounded-3xl border border-gray-100 bg-white">
          <Loader compact label="Loading related products" sublabel="Finding similar fresh picks." />
        </div>
      ) : products.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-5 lg:grid-cols-5">
          {products.map((item) => (
            <ProductCard key={item.product_id} product={item} />
          ))}
        </div>
      ) : (
        <div className="rounded-3xl border border-dashed border-gray-200 bg-white py-12 text-center">
          <p className="text-sm font-semibold text-gray-500">No related products found.</p>
        </div>
      )}
    </section>
  );
};

export default RelatedProductsSection;
